import React, { createRef, useCallback, useEffect, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { ActivityIndicator, RadioButton, TouchableRipple, useTheme } from "react-native-paper";
import { Stack } from "../../components/layout/stack";
import { ErrorMessage, InputAccessoryNavigation, SectionHeadline, Text } from "../../components";
import { ThemeType } from "../../theme/theme";
import { Dropdown } from "../../components/dropdown/dropdown";
import { ReservationService } from "../../api/ReservationService";
import { DropdownType, ServiceRequestFields, AccountSystemTypes } from "../../enums";
import { translate } from "../../i18n";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { serviceRequestSchema, yardiServiceRequestSchema } from "../home/schemas";
import { Inline } from "../../components/layout/inline";
import {
  formatCognitoPhoneNumber,
  formatPhoneNumber,
  formatPhoneString,
} from "../../utils/phoneNumber";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { FullScreenLoader } from "../../components/new/loader/full-screen-loader";
import {
  Dropdown as DropdownUiComponents,
  theme as themeUiComponents,
  TextInput as TextInputUiComponents,
  Button,
} from "ui-components";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { Grid, Container, Row, Column } from "ui-grid";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { convertHexToRGBA } from "../../utils/general";
import automationTestingLocators from "../../screens/new/utils/automation-testing-locators.json";


const { HAS_SICKNESS, HAS_PERMISSION, HAS_PETS } = ServiceRequestFields;

export const CreateServiceRequest = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const { params } = route;
  const { reservationId, propName, propertyUnit, currentPropertyProfile } = params;
  const inputAccessoryViewID = "inputAccessoryViewID1";
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [categoryItems, setCategoryItems] = useState<any>();
  const [subcategoryItems, setSubcategoryItems] = useState<any>();
  const [phoneString, setPhoneString] = useState();
  const [categoryValue, setCategoryValue] = useState();
  const [categoryDescription, setCategoryDescription] = useState();
  const [subcategoryValue, setSubcategoryValue] = useState();
  const [briefDescription, setBriefDescription] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isButtonLoading, setButtonLoading] = useState<boolean>(false);
  const ref = [createRef(), createRef(), createRef()];
  const [scrollViewRef, setScrollViewRef] = useState<any>();
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const logger = useSelector((state: RootState) => state.logger);
  const reservationService = new ReservationService(cognitoUser, dispatch);
  const briefDescriptionCount = briefDescription.length;
  const descriptionCount = description.length;
  const isYardi = currentPropertyProfile.account_system_type === AccountSystemTypes.YARDI;
  const schema = isYardi ? yardiServiceRequestSchema : serviceRequestSchema;

  const {
    register,
    setValue,
    reset,
    trigger,
    clearErrors,
    setError,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    register(ServiceRequestFields.CATEGORY);
    register(ServiceRequestFields.HAS_SICKNESS);
    register(ServiceRequestFields.HAS_PERMISSION);
    register(ServiceRequestFields.PHONE);
    register(ServiceRequestFields.DESCRIPTION);
    if (isYardi) register(ServiceRequestFields.BRIEF_DESCRIPTION);
    if (isYardi) register(ServiceRequestFields.SUB_CATEGORY);
    if (isYardi) register(ServiceRequestFields.HAS_PETS);
  }, [register]);

  const layoutEffect = useCallback(() => {
    (async () => {
      try {
        setLoading(true);
        let categories = await reservationService.getServiceRequestCategories(reservationId);
        if (!Array.isArray(categories)) {
          throw new Error("Could not get categories for service request");
        }
        categories = categories?.map(({ id, label, subcategories }: any) => ({
          value: id,
          label: label?.replace("&amp;", "&"),
          subcategories,
        }));
        setCategoryItems(categories);
      } catch (error) {
        logger.error(error);
      }
      setLoading(false);
    })();
  }, []);
  useFocusEffect(layoutEffect);

  const getRadioOptionStatus = (field: string, value: boolean) => {
    const fieldValue = getValues(field);
    if (fieldValue === value) return "checked";
  };

  const handleCategoryFieldUpdate = (val: any) => {
    clearErrors(ServiceRequestFields.CATEGORY);
    setValue(ServiceRequestFields.CATEGORY, val);
    handleSubcategoryFieldUpdate(undefined);
    const selectedCategory = categoryItems.find(({ value }: any) => value === val);
    setCategoryValue(Platform.OS === "android" ? selectedCategory?.value : selectedCategory?.value);
    setCategoryDescription(
      Platform.OS === "android" ? selectedCategory?.value : selectedCategory?.label
    );
    const subcategories = selectedCategory?.subcategories || [];
    setSubcategoryItems(subcategories);
  };

  const handleSubcategoryFieldUpdate = (val: any) => {
    clearErrors(ServiceRequestFields.SUB_CATEGORY);
    setValue(ServiceRequestFields.SUB_CATEGORY, val);
    setSubcategoryValue(val);
  };

  const handlePhoneFieldUpdate = (val: string) => {
    const formatted = formatPhoneString(val);
    const numberVal = formatCognitoPhoneNumber(val);
    setPhoneString(formatted);
    clearErrors(ServiceRequestFields.PHONE);
    setValue(ServiceRequestFields.PHONE, numberVal);
  };

  const handleDescriptionUpdate = (field: string, val: string) => {
    setDescription(val);
    handleFieldUpdate(field, val);
  };

  const handleBriefDescriptionUpdate = (field: string, val: string) => {
    setBriefDescription(val);
    handleFieldUpdate(field, val);
  };

  const handleContentSizeChange = () => {
    if (scrollViewRef) scrollViewRef.scrollToEnd();
  };

  const handleFieldUpdate = (field: string, val: any) => {
    clearErrors(field);
    setValue(field, val);
  };

  const onSubmitHandler = async () => {
    const serviceRequest = getValues();
    const {
      category,
      subcategory,
      is_household_sick,
      has_permission,
      phone,
      description,
      has_pets,
    } = serviceRequest;
    serviceRequest.category_description = categoryDescription;
    serviceRequest.phone = formatPhoneNumber(phone);
    let phoneNumber = formatPhoneNumber(phone);
    const payload = {
      reservationId,
      isHouseholdSick: is_household_sick,
      hasPermission: has_permission,
      hasPets: has_pets,
      isYardi,
      category,
      subcategory,
      phone: phoneNumber,
      isSuccessful: true,
      propName,
      propertyUnit,
    };
    try {
      setButtonLoading(true);
      await reservationService.createServiceRequest(reservationId, serviceRequest);
      setCategoryValue(undefined);
      setCategoryDescription(undefined);
      setSubcategoryValue(undefined);
      setPhoneString(undefined);
      setBriefDescription("");
      setDescription("");
      reset(); // form
      navigation.navigate("ServiceRequestSummaryScreen", {
        ...params,
        ...payload,
        isSuccessful: true,
      });
    } catch (error) {
      logger.error(error);
      // console.log({ error });
      navigation.navigate("ServiceRequestSummaryScreen", {
        ...params,
        ...payload,
        isSuccessful: false,
      });
    }
    setButtonLoading(false);
  };

  const renderSubCategoryField = () => {
    const isDisabled = !subcategoryItems || !subcategoryItems.length;
    if (isYardi) {
      return (
        <>
          <Text
            tx="serviceStack.serviceRequestSubcategoryLabel"
            style={styles.boldText}
            {...automationTestingLocators.residentServiceRequestSubmit.serviceSubCategory}
          />
          <View style={styles.dropdownContainer}>
            <DropdownUiComponents
              onValueChange={(item: any) => handleSubcategoryFieldUpdate(item)}
              selectedValue={subcategoryValue}
              textInputProps={{ error: errors[ServiceRequestFields.SUB_CATEGORY] }}
              pickerItem={subcategoryItems}
              dropdownEnabled={!isDisabled}
              accessibilityLabel={
                translate("serviceStack.serviceRequestSubcategoryDropdown") as string
              }
              placeholder={translate("serviceStack.serviceRequestSubcategoryPlaceholder") as string}
            />
            {Platform.OS !== "ios" && (
              <>
                <View style={{ marginTop: -8, marginLeft: -8 }}>
                  <ErrorMessage errors={errors} formName={ServiceRequestFields.SUB_CATEGORY} />
                </View>
              </>
            )}
          </View>
        </>
      );
    }
    return null;
  };

  const renderPetsField = () => {
    if (isYardi) {
      return (
        <>
          <Stack space="smaller" style={styles.formSection}>
            <Text
              tx="serviceStack.petsQuestion"
              style={styles.boldText}
              {...automationTestingLocators.residentServiceRequestSubmit.pets}
            />
            <RadioButtonRowContainer
              fieldName={HAS_PETS}
              getStatus={getRadioOptionStatus}
              updateHandler={handleFieldUpdate}
              error={errors[HAS_PETS]}
              theme={theme}
              styles={styles}
            />
          </Stack>
        </>
      );
    }
    return null;
  };

  const renderBriefDescriptionField = () => {
    if (isYardi) {
      return (
        <>
          <Stack space="none" style={[styles.sendToBack, styles.formSection]}>
            <TextInputUiComponents
            {...automationTestingLocators.residentServiceRequestSubmit.summary}
              label={translate("serviceStack.briefDescriptionOfIssue") as string}
              placeholder={translate("serviceStack.briefDescriptionPlaceholder") as string}
              inputAccessoryViewID={inputAccessoryViewID}
              onChangeText={(val: string) =>
                handleBriefDescriptionUpdate(ServiceRequestFields.BRIEF_DESCRIPTION, val)
              }
              onFocus={() => {
                setCurrentInputIndex(1);
                handleContentSizeChange();
              }}
              ref={ref[1] as any}
              onSubmitEditing={() => (ref[2].current as any).focus()}
              returnKeyType="next"
              maxLength={35}
              error={errors[ServiceRequestFields.BRIEF_DESCRIPTION]}
            />
            <Text style={[styles.text, { textAlign: "right" }]}>{briefDescriptionCount} / 35</Text>
          </Stack>
        </>
      );
    }
    return null;
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={Platform.OS === "android" ? 120 : 155}>
      <SafeAreaView style={{ flex: 1 }}>
        {isLoading ? (
          <ActivityIndicator style={styles.loading} />
        ) : (
          <>
            <ScrollView
              showsVerticalScrollIndicator={false}
              ref={(ref) => setScrollViewRef(ref)}
              style={styles.scrollView}
              contentContainerStyle={styles.scrollableArea}>
              <Grid>
                <Container>
                  <View style={styles.inner}>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column
                        span={{ xs: 12, sm: 8, md: 8, lg: 8 }}
                        style={[styles.inputRow, styles.header, styles.headerPadding]}>
                        <SectionHeadline tx="serviceStack.requestDetails" />
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <Text
                            tx="serviceStack.requestEmergencyDetails"
                            style={[styles.smallText, styles.formSection]}
                          />
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <View style={styles.dropdownContainer}>
                            <Text
                              tx="serviceStack.serviceRequestCategoryLabel"
                              style={styles.boldText}
                              {...automationTestingLocators.residentServiceRequestSubmit
                                .serviceCategory}
                            />
                            <DropdownUiComponents
                              onValueChange={(item: any) => handleCategoryFieldUpdate(item)}
                              selectedValue={categoryValue}
                              textInputProps={{ error: errors[ServiceRequestFields.CATEGORY] }}
                              pickerItem={categoryItems}
                              accessibilityLabel={
                                translate("serviceStack.serviceRequestCategoryDropdown") as string
                              }
                              placeholder={
                                translate(
                                  "serviceStack.serviceRequestCategoryPlaceholder"
                                ) as string
                              }
                            />
                            {Platform.OS !== "ios" && (
                              <>
                                <View style={{ marginTop: -8, marginLeft: -8 }}>
                                  <ErrorMessage
                                    errors={errors}
                                    formName={ServiceRequestFields.CATEGORY}
                                  />
                                </View>
                              </>
                            )}
                          </View>
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>{renderSubCategoryField()}</View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <Stack space="smaller" style={[styles.sendToBack, styles.formSection]}>
                            <Text tx="serviceStack.sicknessQuestion" style={styles.boldText} />
                            <Text
                              tx="serviceStack.sicknessQuestionSecondary"
                              style={styles.smallText}
                              {...automationTestingLocators.residentServiceRequestSubmit.sickness}
                            />
                            <RadioButtonRowContainer
                              fieldName={HAS_SICKNESS}
                              getStatus={getRadioOptionStatus}
                              updateHandler={handleFieldUpdate}
                              error={errors[HAS_SICKNESS]}
                              theme={theme}
                              styles={styles}
                            />
                          </Stack>
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>{renderPetsField()}</View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <Stack space="smaller" style={[styles.sendToBack, styles.formSection]}>
                            <Text
                              tx="serviceStack.permissionQuestion"
                              style={styles.boldText}
                              {...automationTestingLocators.residentServiceRequestSubmit.permission}
                            />
                            {getValues(HAS_PERMISSION) === false && (
                              <Text
                                tx="serviceStack.permissionQuestionSecondary"
                                style={styles.smallText}
                              />
                            )}
                            <RadioButtonRowContainer
                              fieldName={HAS_PERMISSION}
                              getStatus={getRadioOptionStatus}
                              updateHandler={handleFieldUpdate}
                              error={errors[HAS_PERMISSION]}
                              theme={theme}
                              styles={styles}
                            />
                          </Stack>
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <Stack space="none" style={[styles.sendToBack, styles.formSection]}>
                            <TextInputUiComponents
                              {...automationTestingLocators.residentServiceRequestSubmit
                                .phoneNumber}
                              label={translate("serviceStack.phoneNumber") as string}
                              value={phoneString}
                              placeholder={
                                translate("componentStatic.phoneNumberPlaceholder") as string
                              }
                              keyboardType="phone-pad"
                              inputAccessoryViewID={inputAccessoryViewID}
                              onChangeText={handlePhoneFieldUpdate}
                              onFocus={() => setCurrentInputIndex(0)}
                              ref={ref[0] as any}
                              onSubmitEditing={() => (ref[1].current as any).focus()}
                              returnKeyType="next"
                              error={errors[ServiceRequestFields.PHONE]}
                            />
                          </Stack>
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>{renderBriefDescriptionField()}</View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                    <Row>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                      <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.inputRow]}>
                        <View style={styles.inputRowInner}>
                          <Stack space="none" style={[styles.sendToBack, styles.formSection]}>
                            <TextInputUiComponents
                              {...automationTestingLocators.residentServiceRequestSubmit
                                .description}
                              label={translate("serviceStack.descriptionOfIssue") as string}
                              placeholder={
                                translate("serviceStack.descriptionPlaceholder") as string
                              }
                              inputAccessoryViewID={inputAccessoryViewID}
                              onChangeText={(val: string) =>
                                handleDescriptionUpdate(ServiceRequestFields.DESCRIPTION, val)
                              }
                              onFocus={() => {
                                setCurrentInputIndex(!isYardi ? 1 : 2);
                                handleContentSizeChange();
                              }}
                              ref={!isYardi ? (ref[1] as any) : (ref[2] as any)}
                              onSubmitEditing={handleSubmit(onSubmitHandler)}
                              returnKeyType="go"
                              maxLength={500}
                              error={errors[ServiceRequestFields.DESCRIPTION]}
                              multiline
                              numberOfLines={3}
                              placeholderStyle={{ paddingTop: 15 }}
                            />
                            <Text style={[styles.text, { textAlign: "right" }]}>
                              {descriptionCount} / 500
                            </Text>
                          </Stack>
                        </View>
                      </Column>
                      <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    </Row>
                  </View>
                </Container>
              </Grid>
            </ScrollView>
            <View style={styles.bottomNav} />

            <BottomNav backgroundColor={theme.colors.white}>
              <View style={{ marginTop: 5 }} />
              <Row>
                <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
                <Column span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
                  <Button
                    text={translate("serviceStack.submitRequest") as string}
                    mode="contained"
                    loading={isButtonLoading}
                    disabled={isButtonLoading}
                    onPress={handleSubmit(onSubmitHandler)}
                    textStyle={{ fontFamily: "Lato_700Bold" }}
                    {...automationTestingLocators.residentServiceRequestSubmit.submitRequest}
                  />
                </Column>
                <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
              </Row>
            </BottomNav>
            {Platform.OS === "ios" ? (
              <InputAccessoryNavigation
                inputAccessoryViewID={inputAccessoryViewID}
                ref={ref as any}
                currentInputIndex={currentInputIndex}
              />
            ) : null}
          </>
        )}
        <Footer />

        <FullScreenLoader loading={isButtonLoading} />
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      paddingTop: spacing.small,
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: theme.colors.white,
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: theme.colors.white,
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: theme.colors.white,
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    inner: {
      justifyContent: "flex-end",
    },
    loading: {
      paddingVertical: spacing.large,
    },
    sendToBack: {
      zIndex: -1,
      elevation: -1,
    },
    dropdownContainer: {
      marginBottom: spacing.medium,
    },
    paddedSection: {},
    formSection: {
      marginBottom: spacing.medium,
    },
    boldText: {
      fontSize: fontSize.medium,
      fontWeight: "bold",
    },
    smallText: {
      fontSize: fontSize.small,
    },
    text: {
      fontSize: fontSize.medium,
    },
    errorText: {
      fontSize: fontSize.small,
      color: colors.error,
    },
    radioButtonContainer: {
      flexDirection: "row",
      justifyContent: "flex-start",
    },
    scrollView: { flexGrow: 0 },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: theme.colors.white, paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        iosPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
        androidPhone: { backgroundColor: theme.colors.white, paddingBottom: 0 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10, marginTop: 15 },
      }),
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: {},
        androidTablet: {
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
    inputRowInner: {
      justifyContent: "center",
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        iosPhone: { paddingVertical: 3 },
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
        androidPhone: { paddingVertical: 3 },
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          backgroundColor: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
        },
      }),
    },
    headerPadding: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingHorizontal: 0,
    },
    bottomNav: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: { flex: 1 },
        iosTablet: {},
        androidPhone: { flex: 1 },
        androidTablet: {},
      }),
    },
    removeBlueOutline: {
      borderRadius: 50,
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });
};

const RadioButtonContainer = ({ status, tx, onPress, theme, styles }: any) => (
  <Inline space="tiny" style={styles.radioButtonContainer}>
    <TouchableRipple
      style={styles.removeBlueOutline}
      rippleColor={convertHexToRGBA(themeUiComponents?.colors?.primary, 0.5)}
      onPress={onPress}
      borderless
      accessibilityLabel={`${translate(tx)} ${translate("componentStatic.radioButton")}`}>
      <View pointerEvents="none">
        <RadioButton.Android
          accessible={true}
          accessibilityLabel={`${translate(tx)} ${translate("componentStatic.radioButton")}`}
          color={themeUiComponents?.colors?.primary}
          value="1"
          status={status}
          onPress={onPress}
        />
      </View>
    </TouchableRipple>
    <Text tx={tx} style={styles.text} />
  </Inline>
);

interface RadioButtonRowContainer {
  fieldName: string;
  getStatus: Function;
  updateHandler: Function;
  error: any;
  theme: any;
  styles: any;
}

const RadioButtonRowContainer = ({
  fieldName,
  getStatus,
  updateHandler,
  error,
  theme,
  styles,
}: RadioButtonRowContainer) => (
  <>
    {error && error.message && <Text style={styles.errorText}>{error.message}</Text>}
    <Inline space="huge">
      <RadioButtonContainer
        status={getStatus(fieldName, true)}
        onPress={() => updateHandler(fieldName, true)}
        tx="componentStatic.yes"
        theme={theme}
        styles={styles}
      />
      <RadioButtonContainer
        status={getStatus(fieldName, false)}
        onPress={() => updateHandler(fieldName, false)}
        tx="componentStatic.no"
        theme={theme}
        styles={styles}
      />
    </Inline>
  </>
);